import {
    Directive,
    Host,
    Optional,
    Renderer2,
    Self,
    ViewContainerRef,
    Input,
    HostListener
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatButton } from "@angular/material/button";

interface PageObject {
    length: number;
    pageIndex: number;
    pageSize: number;
    previousPageIndex: number;
}

@Directive({
    selector: "[paginator-style]"
})
export class PaginatorStyleDirective {
    private pageGapTxt = "...";
    private rangeStart: number;
    private rangeEnd: number;
    private buttonList = [];
    private currentPageObj: PageObject = {
        length: 0,
        pageIndex: 0,
        pageSize: 0,
        previousPageIndex: 0
    };

    private showInitialPages = 2;
    @Input()
    get showTotalPages(): number {
        return this.showInitialPages;
    }

    set showTotalPages(value: number) {
        this.showInitialPages = value;
    }

    get increment(): number {
        return this.showInitialPages % 2 == 0
            ? this.showTotalPages / 2
            : (this.showTotalPages - 1) / 2;
    }

    get numOfPages(): number {
        return this.matPage.getNumberOfPages();
    }

    get lastPageIndex(): number {
        return this.matPage.getNumberOfPages() - 1;
    }

    constructor(
        @Host() @Self() @Optional() private readonly matPage: MatPaginator,
        private viewContainer: ViewContainerRef,
        private renderer: Renderer2
    ) {
        this.matPage.page.subscribe((pageObj: PageObject) => {
            if (
                this.currentPageObj.pageSize != pageObj.pageSize &&
                this.currentPageObj.pageIndex != 0
            ) {
                pageObj.pageIndex = 0;
                this.rangeStart = 0;
                this.rangeEnd = this.showInitialPages - 1;
            }
            this.currentPageObj = pageObj;
            this.initPageRange();
        });
    }

    private buildPageNumbers() {
        const actionContainer = this.viewContainer.element.nativeElement.querySelector(
            "div.mat-paginator-range-actions"
        );
        const nextPageNode = this.viewContainer.element.nativeElement.querySelector(
            "button.mat-paginator-navigation-next"
        );

        if (this.buttonList.length > 0) {
            this.buttonList.forEach(button => {
                this.renderer.removeChild(actionContainer, button);
            });
            this.buttonList.length = 0;
        }
        for (let i = 0; i < this.numOfPages; i++) {
            if (i >= this.rangeStart && i <= this.rangeEnd) {
                this.renderer.insertBefore(
                    actionContainer,
                    this.createButton(i, this.matPage.pageIndex),
                    nextPageNode
                );
            }
        }
    }

    private createButton(i: any, pageIndex: number): any {
        const linkBtn: MatButton = this.renderer.createElement("mat-button");

        if (i === pageIndex) {
            this.renderer.addClass(linkBtn, "mat-mini-fab");
            this.renderer.addClass(linkBtn, "elavation-z0");
        } else this.renderer.addClass(linkBtn, "mat-button");

        const pagingTxt = isNaN(i) ? this.pageGapTxt : +(i + 1);
        const text = this.renderer.createText(pagingTxt + "");

        this.renderer.addClass(linkBtn, "mat-custom-page");
        switch (i) {
            case pageIndex:
                this.renderer.setAttribute(linkBtn, "disabled", "disabled");
                break;
            case this.pageGapTxt:
                let newIndex = this.currentPageObj.pageIndex + this.showInitialPages;

                if (newIndex >= this.numOfPages) newIndex = this.lastPageIndex;

                if (pageIndex != this.lastPageIndex) {
                    this.renderer.listen(linkBtn, "click", () => {
                        this.switchPage(newIndex);
                    });
                }

                if (pageIndex == this.lastPageIndex) {
                    this.renderer.setAttribute(linkBtn, "disabled", "disabled");
                }
                break;
            default:
                this.renderer.listen(linkBtn, "click", () => {
                    this.switchPage(i);
                });
                break;
        }

        this.renderer.appendChild(linkBtn, text);
        this.buttonList.push(linkBtn);
        return linkBtn;
    }
    private initPageRange(): void {
        const middleIndex = (this.rangeStart + this.rangeEnd) / 2;

        this.rangeStart = this.calcRangeStart(middleIndex);
        this.rangeEnd = this.calcRangeEnd(middleIndex);

        this.buildPageNumbers();
    }

    private calcRangeStart(middleIndex: number): number {
        switch (true) {
            case this.currentPageObj.pageIndex == 0 && this.rangeStart != 0:
                return 0;
            case this.currentPageObj.pageIndex > this.rangeEnd:
                return this.currentPageObj.pageIndex + this.increment > this.lastPageIndex
                    ? this.lastPageIndex - this.increment * 2
                    : this.currentPageObj.pageIndex - this.increment;
            case this.currentPageObj.pageIndex > this.currentPageObj.previousPageIndex &&
                this.currentPageObj.pageIndex > middleIndex &&
                this.rangeEnd < this.lastPageIndex:
                return this.rangeStart + 1;
            case this.currentPageObj.pageIndex < this.currentPageObj.previousPageIndex &&
                this.currentPageObj.pageIndex < middleIndex &&
                this.rangeStart > 0:
                return this.rangeStart - 1;
            default:
                return this.rangeStart;
        }
    }

    private calcRangeEnd(middleIndex: number): number {
        switch (true) {
            case this.currentPageObj.pageIndex == 0 &&
                this.rangeEnd != this.showInitialPages:
                return this.showInitialPages - 1;
            case this.currentPageObj.pageIndex > this.rangeEnd:
                return this.currentPageObj.pageIndex + this.increment > this.lastPageIndex
                    ? this.lastPageIndex
                    : this.currentPageObj.pageIndex + 1;
            case this.currentPageObj.pageIndex > this.currentPageObj.previousPageIndex &&
                this.currentPageObj.pageIndex > middleIndex &&
                this.rangeEnd < this.lastPageIndex:
                return this.rangeEnd + 1;
            case this.currentPageObj.pageIndex < this.currentPageObj.previousPageIndex &&
                this.currentPageObj.pageIndex < middleIndex &&
                this.rangeStart >= 0 &&
                this.rangeEnd > this.showInitialPages - 1:
                return this.rangeEnd - 1;
            default:
                return this.rangeEnd;
        }
    }

    private switchPage(index: number): void {
        const previousPageIndex = this.matPage.pageIndex;
        this.matPage.pageIndex = index;
        this.matPage["_emitPageEvent"](previousPageIndex);
        this.initPageRange();
    }

    @HostListener('window:resize', ['$event']) 
    onChangeScreenSize(event) {
        this.rangeStart = 0;
        this.rangeEnd = this.showInitialPages - 1;
        this.initPageRange();
    }

    public ngAfterViewInit() {
        this.rangeStart = 0;
        this.rangeEnd = this.showInitialPages - 1;
        this.initPageRange();
    }
}
